import { create } from 'zustand';
import { getErrorMessage } from '../utils/messages';
import { IAuthStore, ICreateAccount } from '../interfaces/auth.interface';
import { IUser } from '../interfaces/user.interface';
import {
    removeAllCookies,
    setAuthCookie,
    setAuthCookies,
} from '../cookies/auth.cookies';
import {
    login,
    signup,
    loginWithToken,
    validateEmail,
    deliveryMarketingData,
} from '../api/auth.api';
import { ESession } from '../enums/auth.enum';

export const useAuthStore = create<IAuthStore>((set) => ({
    token: null,
    emailExist: null,
    mustAnswerQuestion: false,
    data: null,
    loading: false,
    error: null,
    setLoading: (loading: boolean) => set({ loading }),
    setError: (error: string | null) => set({ error }),
    setData: (data: IUser | null) => set({ data }),
    setEmailExist: (emailExist: boolean) => set({ emailExist }),
    setMustAnswerQuestion: (mustAnswerQuestion: boolean) => set({ mustAnswerQuestion }),
}));

export const startValidateEmail = async (email: string) => {
    try {
        useAuthStore.setState(() => ({ loading: true }));
        const { data }: any = await validateEmail(email);
        useAuthStore.setState(() => ({
            emailExist: data as boolean,
            loading: false,
        }));
    } catch (error: any) {
        console.error('[startValidateEmail] Error', error);
        useAuthStore.setState(() => ({
            emailExist: false,
            loading: false,
        }));
    }
};

export const startLogin = async (email: string, password: string) => {
    try {
        useAuthStore.setState(() => ({ loading: true }));
        const { data }: any = await login({ email, password });
        setAuthCookies({
            token: data.token,
            userID: data._id,
        });
        useAuthStore.setState(() => ({
            data,
            loading: false,
        }));
    } catch (error: any) {
        console.error('[startLogin] Error', error);
        const errorMessage = getErrorMessage(error.message);
        useAuthStore.setState(() => ({ loading: false, error: errorMessage }));
    }
};

export const startLoadUser = async (token: string) => {
    useAuthStore.setState(() => ({ loading: true }));
    const { data }: any = await loginWithToken(token);
    useAuthStore.setState(() => ({
        data,
        loading: false,
    }));
};

export const startLogout = async () => {
    useAuthStore.setState(() => ({ loading: true }));
    removeAllCookies();
    useAuthStore.setState(() => ({ data: null, loading: false }));
};

export const startSignup = async (payload: ICreateAccount) => {
    try {
        useAuthStore.setState(() => ({ loading: true }));
        const { data }: any = await signup(payload);
        setAuthCookies({
            token: data.token,
            userID: data._id,
        });
        setAuthCookie(ESession.MUST_ANSWER_QUESTION, true);
        useAuthStore.setState(() => ({
            data,
            mustAnswerQuestion: true,
            token: data.token,
            loading: false,
        }));
        setAuthCookie(ESession.MUST_ANSWER_QUESTION, true);
    } catch (error: any) {
        console.error('[startSignup] Error', error);
        useAuthStore.setState(() => ({ loading: false, error: error.message }));
    }
};

export const startLoadMarketingData = async (values: any) => {
    try {
        useAuthStore.setState(() => ({ loading: true }));
        await deliveryMarketingData(values);
        useAuthStore.setState(() => ({
            mustAnswerQuestion: false,
            loading: false,
        }));
        setAuthCookie(ESession.MUST_ANSWER_QUESTION, false);
    } catch (error: any) {
        console.error('[startLoadMarketingData] Error', error);
        useAuthStore.setState(() => ({ loading: false, error: error.message }));
    }
};
